import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, Grid } from '@factorialco/deprecated-design-system'
import {
  ContractVersionPageContractVersionFragment,
  ContractVersionsPageContractVersionRequestFragment,
  ContractVersionsPageEmployeeActivityFragment,
  ContractVersionsPageEmployeeFragment,
  ContractVersionsPageIncidenceRuleFragment,
  ContractVersionsPageQuery,
  WorkingConditionVersionApplicationFragment,
} from 'generated/resources/graphql'
import { observable } from 'mobx'
import moment from 'moment'

import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { stringToDate } from 'modules/core/lib/dates'

import { FLOWS } from 'modules/contracts/components/FlowSelectorModal/constants'
import NewContractHistory from 'modules/contracts/components/NewContractHistory'
import useEmployeeContracts from 'modules/contracts/hooks/useEmployeeContracts'
import useFetchContractHistoryData from 'modules/contracts/hooks/useFetchContractHistoryData'
import { ContractCuesProvider } from 'modules/contracts/lib/contractCuesContext'
import { ContractHistoryDataProvider } from 'modules/contracts/lib/contractHistoryDataContext'

import AboutTheRequest from './AboutTheRequest'
import Compensation from './Compensation'
import ContractInformation from './ContractInformation'
import FixDiscontinuous from './FixDiscontinuous'
import Header from './Header'
import History from './History'
import JobPosition from './JobPosition'
import LegalAndLabour from './LegalAndLabour'
import ModalSelector from './ModalSelector'
import OtherContractInformation from './OtherContractInformation'
import Time from './Time'

interface Props {
  features: ContractVersionsPageQuery['features']['featuresConnection']['nodes']
  employee: ContractVersionsPageEmployeeFragment
  selectedContractVersion: ContractVersionPageContractVersionFragment
  setSelectedContractVersion: (contractVersionId: number | undefined) => void
  employeeContractVersions: ContractVersionPageContractVersionFragment[]

  workingConditionVersionApplication?: WorkingConditionVersionApplicationFragment
  contractVersionRequest: ContractVersionsPageContractVersionRequestFragment | undefined
  employeeActivity?: ContractVersionsPageEmployeeActivityFragment | undefined
  incidenceRules: ContractVersionsPageIncidenceRuleFragment[]
  isAutoApprover: boolean
  hasTimeBalance: boolean
  hasTimeTrackingAnnualisationDailyValue: boolean
  hasFixedDiscontinuousContracts?: boolean
  hasLegacyJobCatalog?: boolean
  canSeeContractExtraInfo?: boolean
}

const ContractDetails = ({
  features,
  employee,
  selectedContractVersion,
  setSelectedContractVersion,
  employeeContractVersions,
  workingConditionVersionApplication,
  contractVersionRequest,
  employeeActivity,
  incidenceRules,
  isAutoApprover,
  hasTimeBalance,
  hasTimeTrackingAnnualisationDailyValue,
  hasFixedDiscontinuousContracts,
  hasLegacyJobCatalog,
  canSeeContractExtraInfo,
}: Props) => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const [showModal, setShowModal] = React.useState(false)
  const [showHistory, setShowHistory] = React.useState(false)
  const [option, setOption] = React.useState(FLOWS.promotion)
  const employeeId = employee?.id
  const legalEntity = employee?.legalEntity

  const professionalsActivated = features.find(
    (feature) => feature.name === 'factorial_professionals'
  )?.enabled
  const hasDevFixedDiscontinuousContracts =
    features.find((feature) => feature.name === 'dev_fixed_discontinuous_contracts')?.enabled ??
    false
  const hasDevTimeTrackingWorkableDaysDefinition =
    features.find((feature) => feature.name === 'dev_time_tracking_workable_days_definition')
      ?.enabled ?? false

  const listOfFields = useLazyRef(() => observable.map(new Map())).current

  const contractVersionIds = employeeContractVersions.map((cv) => cv.id).filter(Boolean)
  const { additionalCompensations, customFieldValues, newAdditionalCompensations } =
    useFetchContractHistoryData(contractVersionIds)

  const { contracts: employeeContracts } = useEmployeeContracts(
    selectedContractVersion.id ?? 0,
    employeeContractVersions
  )

  const onClickFix = () => {
    if (!contractVersionRequest) {
      if (!selectedContractVersion?.id || !employeeId) return

      navigate(
        navigation.employees.id.contractVersions.fix.fullPath({
          id: employeeId.toString(),
          contractVersionId: selectedContractVersion.id.toString(),
        })
      )
    } else {
      setShowModal(true)
    }
  }

  const onClickDelete = () => {
    if (contractVersionRequest) {
      setShowModal(true)
    }
  }

  const onCloseModal = () => setShowModal(false)

  const onClickEdit = () => setShowModal(true)

  const toggleHistory = () => {
    if (!showHistory) track(trackingEvents.employee.contracts.startContractHistory)
    setShowHistory(!showHistory)
  }

  const status = selectedContractVersion.contract?.status
  // Default to true as companies with fixed discontinuous contracts need to keep working as previously.
  const isContractActive =
    selectedContractVersion.contract?.isDiscontinuous && status ? status.isActive : true
  const isItalianLegalEntity =
    legalEntity?.country === 'it' && selectedContractVersion.frForfaitJours
  const showLegalAndLabour =
    canSeeContractExtraInfo && (selectedContractVersion.country !== 'pt' || isItalianLegalEntity)

  const hasCues = (professionalsActivated && incidenceRules.length > 0) ?? false
  const contractVersionPolicies = selectedContractVersion._policies
  const showFixDiscontinuous =
    hasDevFixedDiscontinuousContracts && selectedContractVersion.contract?.isDiscontinuous
  const hasApprovalSystem =
    features.find((feature) => feature.name === 'dev_update_flow_approval_system')?.enabled ?? false
  const hasToRequestUpdate = hasApprovalSystem ? !isAutoApprover : false
  const isEmployeeTerminated =
    !!employee.terminatedOn && stringToDate(employee.terminatedOn, 'backend').isBefore(moment())

  return (
    employeeId && (
      <ContractCuesProvider
        collection={{
          hasCues: hasCues,
          incidenceRules: incidenceRules,
          listOfFields: listOfFields,
        }}
      >
        <ContractHistoryDataProvider
          additionalCompensations={additionalCompensations}
          customFieldValues={customFieldValues}
          newAdditionalCompensations={newAdditionalCompensations}
        >
          <Grid variant="fixed">
            <Box
              style={{
                position: 'relative',
                gridColumnStart: '2',
                gridColumnEnd: 'span 10',
              }}
              paddingBottom="s48"
              gridColSpan={{ xxs: 10 }}
              gap="s40"
            >
              <Header
                employee={employee}
                selectedContractVersion={selectedContractVersion}
                contractVersions={employeeContractVersions}
                contractVersionRequest={contractVersionRequest}
                workingConditionVersionApplication={workingConditionVersionApplication}
                onClickEdit={onClickEdit}
                onClickFix={onClickFix}
                onClickDelete={onClickDelete}
                toggleHistory={toggleHistory}
                hasFixedDiscontinuousContracts={hasFixedDiscontinuousContracts}
                setSelectedContractVersion={setSelectedContractVersion}
              />
              <Box gap="s32">
                {showFixDiscontinuous && contractVersionPolicies && (
                  <FixDiscontinuous
                    contractVersion={selectedContractVersion}
                    contractVersionPolicies={contractVersionPolicies}
                    employeeActivity={employeeActivity}
                    contractData={selectedContractVersion.contract}
                  />
                )}
                <ContractInformation
                  isContractPaused={!isContractActive}
                  contractVersion={selectedContractVersion}
                  isEmployeeTerminated={isEmployeeTerminated}
                />
                <JobPosition
                  employeeId={employee.id}
                  contractVersion={selectedContractVersion}
                  hasLegacyJobCatalog={hasLegacyJobCatalog}
                />
                <Compensation
                  contractVersion={selectedContractVersion}
                  employee={employee}
                  compensations={selectedContractVersion.compensations}
                />
                <Time
                  employeeId={employee.id}
                  contractVersion={selectedContractVersion}
                  workingConditionVersionApplication={workingConditionVersionApplication}
                  hasTimeBalance={hasTimeBalance}
                  hasTimeTrackingAnnualisationDailyValue={hasTimeTrackingAnnualisationDailyValue}
                  hasDevTimeTrackingWorkableDaysDefinition={
                    hasDevTimeTrackingWorkableDaysDefinition
                  }
                />
                {showLegalAndLabour && (
                  <LegalAndLabour
                    contractVersion={selectedContractVersion}
                    isItalianLegalEntity={isItalianLegalEntity}
                  />
                )}
                <OtherContractInformation
                  employeeId={employeeId}
                  contractVersion={selectedContractVersion}
                />
                {contractVersionRequest && (
                  <AboutTheRequest
                    employeeId={employeeId}
                    contractVersionRequest={contractVersionRequest}
                    canPromote={selectedContractVersion._policies?.promote?.can ?? false}
                  />
                )}
              </Box>
            </Box>
          </Grid>
          {hasFixedDiscontinuousContracts ? (
            <NewContractHistory
              open={showHistory}
              employeeId={employeeId}
              onContractVersionSelect={setSelectedContractVersion}
              selectedContractVersionId={selectedContractVersion?.id ?? undefined}
              toggleHistory={toggleHistory}
            />
          ) : (
            <History
              employeeId={employee.id}
              open={showHistory}
              selectedContractVersion={selectedContractVersion ?? undefined}
              employeeContracts={employeeContracts}
              setSelectedGraphqlContractVersion={setSelectedContractVersion}
              toggleHistory={toggleHistory}
            />
          )}
          {showModal && (
            <ModalSelector
              employee={employee}
              onCloseModal={onCloseModal}
              selectedContractVersion={selectedContractVersion}
              allContractVersions={employeeContractVersions}
              setOption={setOption}
              option={option}
              showFixOption={false}
              contractVersionRequest={contractVersionRequest}
              canPromote={selectedContractVersion._policies?.promote?.can ?? false}
              hasToRequestUpdate={hasToRequestUpdate}
            />
          )}
        </ContractHistoryDataProvider>
      </ContractCuesProvider>
    )
  )
}

export default ContractDetails
